import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import type { SimulationResponse, CreateSimulationRequest, SimulationDocumentResponse } from '@/types/simulation.ts';
import type { User } from '@/types/auth.ts';
import { createSimulationSchema } from '@/schemas/simulationSchema.ts';
import type { SimulationFormValues } from '@/schemas/simulationSchema.ts';
import simulationService from '@/services/simulationService.ts';
import teamService from '@/services/teamService.ts';
import { useAlert } from '@/context/AlertContext.tsx';
import { useAuth } from '@/context/AuthContext.tsx';
import { translateDocumentFailure } from '@/utils/documentFailureMessage.ts';
import { useCatalogOptions, type CatalogOption } from '@/hooks/useCatalog.ts';
import { useSimulationDocumentsPoller } from '@/hooks/useSimulationDocumentsPoller.ts';

// Mirrors the backend MaxDocumentsPerSimulation in SimulationDocumentService.
// If the backend limit changes, update here; the backend guard stays as the
// source of truth — this constant just drives preventive UI gating.
const MAX_DOCUMENTS = 5;

type SimulationSubmitPayload = Omit<CreateSimulationRequest, 'assignedSalespersonIds'> & {
  assignedSalespersonIds?: string[];
};

type SupportedLanguage = SimulationFormValues['language'];

function normalizeLanguage(raw: string | undefined | null): SupportedLanguage {
  const v = (raw ?? '').toLowerCase();
  return v === 'it' ? 'it' : 'es';
}

function buildEmptyForm(language: SupportedLanguage): SimulationFormValues {
  return {
    name: '',
    simulationType: '',
    salePhase: '',
    objections: '',
    sector: '',
    subSector: '',
    priceRange: '',
    difficultyLevel: 3,
    assignedMaestroId: '',
    assignedSalespersonIds: [],
    purpose: 'evaluate',
    pitchObjective: '',
    kpisToEvaluate: [],
    language,
  };
}

export interface UseSimulationFormReturn {
  /* Form */
  form: UseFormReturn<SimulationFormValues>;

  /* State */
  simulations: SimulationResponse[];
  salespeople: User[];
  loading: boolean;
  submitting: boolean;
  error: string;
  editingId: string | null;
  existingDocs: SimulationDocumentResponse[];
  pendingFiles: File[];
  uploadingFiles: Set<string>;
  dragOver: boolean;
  rejectedUploads: Array<{ fileName: string; reason: string }>;
  limitReached: boolean;
  hasRejectedUploads: boolean;
  atDocumentLimit: boolean;
  deleteTarget: { id: string; name: string } | null;
  showLeaveConfirm: boolean;

  /* State setters needed by the page */
  setDeleteTarget: React.Dispatch<React.SetStateAction<{ id: string; name: string } | null>>;
  setShowLeaveConfirm: React.Dispatch<React.SetStateAction<boolean>>;
  setDragOver: React.Dispatch<React.SetStateAction<boolean>>;

  /* Handlers */
  resetForm: () => void;
  onSubmit: (data: SimulationFormValues) => Promise<void>;
  handleEdit: (sim: SimulationResponse) => Promise<void>;
  addFiles: (files: File[]) => void;
  handleDrop: (e: React.DragEvent) => void;
  handleFileInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
  removePendingFile: (index: number) => void;
  removeExistingDoc: (doc: SimulationDocumentResponse) => Promise<void>;
  dismissRejectedUpload: (fileName: string) => void;
  clearLimitReached: () => void;
  toggleSalesperson: (id: string) => void;
  handleBack: (navigate: (path: string) => void) => void;
  handleDeleted: () => void;

  /* Derived values */
  salePhases: CatalogOption[];
  sectors: CatalogOption[];
  priceRanges: CatalogOption[];
  simulationTypeOptions: CatalogOption[];
  subSectors: CatalogOption[];
  watchSector: string | undefined;
  watchDifficulty: number;
  watchAssigned: string[];
}

export interface UseSimulationFormOptions {
  /** Flujo MVP: tras crear (y subir documentos) se invoca en lugar de resetear el formulario. */
  onCreated?: (sim: SimulationResponse) => void;
}

export function useSimulationForm(options?: UseSimulationFormOptions): UseSimulationFormReturn {
  const onCreated = options?.onCreated;
  const { t, i18n } = useTranslation();
  const { id: routeEditId } = useParams<{ id: string }>();
  const { showAlert } = useAlert();
  const { isSalesManager } = useAuth();
  const uiLanguage = normalizeLanguage(i18n.resolvedLanguage);

  /* ── State ── */
  const [simulations, setSimulations] = useState<SimulationResponse[]>([]);
  const [salespeople, setSalespeople] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  const [existingDocs, setExistingDocs] = useState<SimulationDocumentResponse[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<Set<string>>(new Set());
  const [dragOver, setDragOver] = useState(false);
  // Synchronous upload rejections (INVALID_FILE_TYPE, FILE_TOO_LARGE, etc.).
  // Live in local state only — the file never reached the backend, so there
  // is nothing to notify about after the form closes. While visible, they
  // block the submit so the manager cannot save without resolving them.
  const [rejectedUploads, setRejectedUploads] = useState<Array<{ fileName: string; reason: string }>>([]);
  const [limitReached, setLimitReached] = useState(false);

  /* ── Form ── */
  const simulationSchema = useMemo(() => createSimulationSchema(t), [t]);

  const form = useForm<SimulationFormValues>({
    resolver: zodResolver(simulationSchema),
    defaultValues: buildEmptyForm(uiLanguage),
  });

  const { watch, setValue, reset } = form;

  /* ── Catalog-driven options ── */
  const { options: salePhases } = useCatalogOptions('SalePhase');
  const { options: sectors } = useCatalogOptions('Sector');
  const { options: priceRanges } = useCatalogOptions('PriceRange');
  const { options: simulationTypeOptions } = useCatalogOptions('SimulationType');
  const { options: allSubSectors } = useCatalogOptions('SubSector');

  const watchSector = watch('sector');
  const watchDifficulty = watch('difficultyLevel');
  const watchAssigned = watch('assignedSalespersonIds') ?? [];
  const subSectors = useMemo(
    () => (watchSector ? allSubSectors.filter((o) => o.parentSlug === watchSector) : []),
    [allSubSectors, watchSector],
  );

  // Poll backend while any existing doc is still processing. No-op when editingId
  // is null (create flow, no simulation yet) or when all docs are terminal.
  useSimulationDocumentsPoller(editingId, existingDocs, setExistingDocs);

  /* ── Handlers ── */
  const resetForm = useCallback(() => {
    reset(buildEmptyForm(uiLanguage));
    setEditingId(null);
    setExistingDocs([]);
    setPendingFiles([]);
    setUploadingFiles(new Set());
    setRejectedUploads([]);
    setLimitReached(false);
    setError('');
  }, [reset, uiLanguage]);

  const handleEdit = useCallback(async (sim: SimulationResponse) => {
    setEditingId(sim.id);
    setPendingFiles([]);
    setRejectedUploads([]);
    setLimitReached(false);
    reset({
      name: sim.name,
      simulationType: sim.simulationType,
      salePhase: sim.salePhase,
      objections: sim.objections ?? '',
      sector: sim.sector ?? '',
      subSector: sim.subSector ?? '',
      priceRange: sim.priceRange ?? '',
      difficultyLevel: sim.difficultyLevel,
      assignedMaestroId: sim.assignedMaestroId ?? '',
      assignedSalespersonIds: sim.assignedSalespersonIds,
      language: normalizeLanguage(sim.language),
    });
    // Load existing documents
    try {
      const docs = await simulationService.getDocuments(sim.id);
      setExistingDocs(docs);
    } catch {
      setExistingDocs([]);
    }
    // Scroll to form
    document.getElementById('simulation-form')?.scrollIntoView({ behavior: 'smooth' });
  }, [reset]);

  const uploadFilesImmediately = useCallback(async (newFiles: File[], simulationId: string) => {
    for (const file of newFiles) {
      const fileKey = `${file.name}-${file.size}`;
      setUploadingFiles((prev) => new Set(prev).add(fileKey));
      try {
        const doc = await simulationService.uploadDocument(simulationId, file);
        setExistingDocs((prev) => [doc, ...prev]);
        setPendingFiles((prev) => prev.filter((f) => f !== file));
      } catch (error) {
        const code = error instanceof Error ? error.message : '';
        // Clear the in-flight pending entry regardless — the file either
        // made it or was rejected; nothing stays as "still uploading".
        setPendingFiles((prev) => prev.filter((f) => f !== file));
        if (code === 'DOCUMENT_LIMIT_REACHED') {
          // Global limit, not tied to one file. Render inline as a banner
          // so the manager can't miss it if they scrolled past.
          setLimitReached(true);
        } else {
          // Per-file rejection: show inline next to the uploader, blocks
          // the submit until dismissed or resolved.
          setRejectedUploads((prev) => [
            ...prev,
            { fileName: file.name, reason: translateDocumentFailure(t, code) },
          ]);
        }
      } finally {
        setUploadingFiles((prev) => {
          const next = new Set(prev);
          next.delete(fileKey);
          return next;
        });
      }
    }
  }, [t]);

  const dismissRejectedUpload = useCallback((fileName: string) => {
    setRejectedUploads((prev) => prev.filter((r) => r.fileName !== fileName));
  }, []);

  const clearLimitReached = useCallback(() => setLimitReached(false), []);

  const addFiles = useCallback((newFiles: File[]) => {
    // Preventive gating against the 5-doc cap. The input is disabled when the
    // count is at the limit, but a drag-and-drop can still reach here, and a
    // single drop might bring more files than the remaining slots.
    const currentCount = existingDocs.length + pendingFiles.length;
    const remaining = Math.max(0, MAX_DOCUMENTS - currentCount);
    const accepted = newFiles.slice(0, remaining);
    const overflow = newFiles.slice(remaining);

    if (overflow.length > 0) {
      const reason = t('configurator.documentLimitExceededReason');
      setRejectedUploads((prev) => [
        ...prev,
        ...overflow.map((f) => ({ fileName: f.name, reason })),
      ]);
    }

    if (accepted.length === 0) return;

    if (editingId) {
      // In edit mode, upload immediately
      setPendingFiles((prev) => [...prev, ...accepted]);
      uploadFilesImmediately(accepted, editingId);
    } else {
      // In create mode, accumulate pending
      setPendingFiles((prev) => [...prev, ...accepted]);
    }
  }, [editingId, uploadFilesImmediately, existingDocs.length, pendingFiles.length, t]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(Array.from(e.dataTransfer.files));
  }, [addFiles]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addFiles(Array.from(e.target.files!));
    }
  }, [addFiles]);

  const removePendingFile = useCallback((index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const removeExistingDoc = useCallback(async (doc: SimulationDocumentResponse) => {
    try {
      await simulationService.deleteDocument(doc.simulationId, doc.id);
      setExistingDocs((prev) => prev.filter((d) => d.id !== doc.id));
    } catch {
      showAlert(t('configurator.deleteDocError'), 'error');
    }
  }, [showAlert, t]);

  const toggleSalesperson = useCallback((id: string) => {
    const current = form.getValues('assignedSalespersonIds') ?? [];
    if (current.includes(id)) {
      setValue('assignedSalespersonIds', current.filter((x) => x !== id));
    } else {
      setValue('assignedSalespersonIds', [...current, id]);
    }
  }, [form, setValue]);

  const handleBack = useCallback((navigate: (path: string) => void) => {
    // Read isDirty at call time, not as dep (changes every keystroke)
    if (form.formState.isDirty || editingId) {
      setShowLeaveConfirm(true);
    } else {
      navigate('/admin/simulations');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingId]);

  const onSubmit = useCallback(async (data: SimulationFormValues) => {
    setError('');
    setSubmitting(true);
    try {
      const payload: SimulationSubmitPayload = {
        name: data.name,
        simulationType: data.simulationType,
        salePhase: data.salePhase,
        objections: data.objections || undefined,
        sector: data.sector || undefined,
        subSector: data.subSector || undefined,
        priceRange: data.priceRange || undefined,
        difficultyLevel: data.difficultyLevel,
        assignedMaestroId: data.assignedMaestroId || undefined,
        assignedSalespersonIds: data.assignedSalespersonIds?.length ? data.assignedSalespersonIds : undefined,
        purpose: data.purpose,
        pitchObjective: data.pitchObjective || undefined,
        kpisToEvaluate: data.kpisToEvaluate?.length ? data.kpisToEvaluate : undefined,
        language: data.language,
      };

      if (editingId) {
        const updated = await simulationService.update(editingId, payload);
        setSimulations((prev) => prev.map((s) => (s.id === editingId ? updated : s)));
        showAlert(t('configurator.simulationUpdated'), 'success');
      } else {
        const created = await simulationService.create(payload);
        setSimulations((prev) => [created, ...prev]);
        // Upload pending files for the newly created simulation
        if (pendingFiles.length > 0) {
          for (const file of pendingFiles) {
            try {
              await simulationService.uploadDocument(created.id, file);
            } catch (error) {
              const code = error instanceof Error ? error.message : '';
              if (code === 'DOCUMENT_LIMIT_REACHED') {
                showAlert(t('configurator.documentLimitError'), 'error');
              } else {
                showAlert(t('configurator.uploadError', { name: file.name }), 'error');
              }
            }
          }
        }
        showAlert(t('configurator.simulationCreated'), 'success');
        if (onCreated) {
          onCreated(created);
          return;
        }
      }
      resetForm();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('configurator.saveError'));
    } finally {
      setSubmitting(false);
    }
  }, [editingId, pendingFiles, resetForm, showAlert, t, onCreated]);

  const handleDeleted = useCallback(() => {
    if (deleteTarget) {
      setSimulations((prev) => prev.filter((s) => s.id !== deleteTarget.id));
      if (editingId === deleteTarget.id) {
        resetForm();
      }
    }
    setDeleteTarget(null);
  }, [deleteTarget, editingId, resetForm]);

  /* ── Effects ── */

  // Load simulations + salespeople on mount. The salespeople list is a
  // manager-only resource (the endpoint is gated to SalesManager/SuperUser),
  // so only request it for managers — otherwise a Salesperson gets a noisy 403.
  useEffect(() => {
    const load = async () => {
      const results = await Promise.allSettled([
        simulationService.getAll(),
        isSalesManager ? teamService.getSalespeople() : Promise.resolve<User[]>([]),
      ]);

      if (results[0].status === 'fulfilled') {
        setSimulations(results[0].value);
      }
      if (results[1].status === 'fulfilled') {
        setSalespeople(results[1].value);
      }

      setLoading(false);
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSalesManager]);

  // Auto-edit from route param /admin/simulations/edit/:id
  const autoEditedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!routeEditId || loading || simulations.length === 0) return;
    if (autoEditedRef.current === routeEditId) return;
    const target = simulations.find((s) => s.id === routeEditId);
    if (target) {
      autoEditedRef.current = routeEditId;
      handleEdit(target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeEditId, loading, simulations]);

  // Reset sub-sector when sector changes or current sub is not valid under new sector.
  // Uses the catalog-driven subSectors list (already filtered by the active sector).
  useEffect(() => {
    if (!watchSector) {
      setValue('subSector', '');
      return;
    }
    const current = watch('subSector');
    if (current && !subSectors.some((s) => s.value === current)) {
      setValue('subSector', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchSector, subSectors]);

  return {
    form,
    simulations,
    salespeople,
    loading,
    submitting,
    error,
    editingId,
    existingDocs,
    pendingFiles,
    uploadingFiles,
    dragOver,
    rejectedUploads,
    limitReached,
    hasRejectedUploads: rejectedUploads.length > 0,
    atDocumentLimit: existingDocs.length + pendingFiles.length >= MAX_DOCUMENTS,
    dismissRejectedUpload,
    clearLimitReached,
    deleteTarget,
    showLeaveConfirm,
    setDeleteTarget,
    setShowLeaveConfirm,
    setDragOver,
    resetForm,
    onSubmit,
    handleEdit,
    addFiles,
    handleDrop,
    handleFileInput,
    removePendingFile,
    removeExistingDoc,
    toggleSalesperson,
    handleBack,
    handleDeleted,
    salePhases,
    sectors,
    priceRanges,
    simulationTypeOptions,
    subSectors,
    watchSector,
    watchDifficulty,
    watchAssigned,
  };
}
